import {
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { CorporateTheme, font, lightTheme } from "../../utils/themes";
import { Column, DataTableProps } from "../../types/types";
import SynconIcon from "../../assets/icons/SynconIcon";
import ViewIcon from "../../assets/icons/ViewIcon";
import EditIcon from "../../assets/icons/EditIcon";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import PlaceholderImage from "../../assets/images/placeholder-image.jpg";
import { SkeletonCell } from "../SkeletonCell";
import NoData from "./NoData";
import { formatColumnLabel, formatDateDefault, getRandomColor, hexToRgba, getPercentage } from "../../utils/helper";
import { getStatusLabel, statusColorMapping } from "../../utils/stausMapping";
import CopyIcon from "../../assets/icons/CopyIcon";
import ShareLinkIcon from "../../assets/icons/ShareLinkIcon";
import { capitalize } from "lodash";
import BlockIcon from "../../assets/icons/BlockIcon";
import StyledToolTip from "../StyledComponents/StyledTooltip";
import TooltipIcon from "../../assets/icons/Tooltip";
import { useTheme } from "../../middleware/ThemeContext";
import { Button } from "../form/Button";
import ContinueIcon from "../../assets/icons/ContinueIcon";
import { useNavigate } from "react-router-dom";
import SimpleBar from "simplebar-react";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import StyledText from "../StyledComponents/StyledText";
import { TruncatedText } from "./TruncatedText";
import { StyledTag } from "../StyledTag";
import StyledCheckBox from "../StyledComponents/CheckBox/StyledCheckBox";
import DuplicateIcon from "../../assets/icons/DuplicateIcon";

// Styled components for table layout and styling
const TableContainer = styled.div`
  overflow-x: auto;
  margin: ${CorporateTheme.spacing.lg} 0;
`;

const Table = styled.table<{ width?: string }>`
  min-width: ${({ width }) => (width ? `${width}px` : "1200px")};
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled('thead')(({ theme }) => ({
  background: theme.CorporateTheme.colors.headerBG,
  height: '45px',
}));

const TableHeaderCell = styled('th')<{ $isDraggable?: boolean }>(
  ({ theme, $isDraggable }) => ({
    padding: CorporateTheme.spacing.sm,
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: '"Raleway", sans-serif',
    color: theme.CorporateTheme.colors.headerColor,
    // textAlign: 'center',
    verticalAlign: 'middle',
    ...($isDraggable
      ? {
        cursor: 'grab',
        userSelect: 'none',
        '&:hover': {
          background: 'rgba(0, 0, 0, 0.02)',
        },
      }
      : {}
    ),
  })
);

export const TableRow = styled('tr')<{ $selected?: boolean, $disableHover?: boolean; }>(({ theme, $selected, $disableHover }) => ({
  borderBottom: `1px solid ${theme?.CorporateTheme?.colors?.border}`,
  cursor: 'pointer',
  // backgroundColor: $selected ? theme?.CorporateTheme?.colors?.headerBG : 'transparent',
  '&:nth-child(even)': {
    backgroundColor: $selected ? '' : theme.CorporateTheme.colors.headerBG,
  },
  '&:nth-child(odd)': {
    backgroundColor: $selected ? '' : theme.CorporateTheme.colors.background,
  },
  '&:hover': {
    backgroundColor: theme?.CorporateTheme?.colors?.headerBG,
  },
  transition: 'all 0.3s ease', // Add this line
}));

export const TableCell = styled('td')<{ $center?: boolean, istableCellSpacing?: boolean, $isNumeric?: boolean }>(
  ({ theme, $center, istableCellSpacing, $isNumeric }) => ({
    padding: istableCellSpacing ? '10px 10px' : '6px 10px',
    // justifyContent: `${$center ? "center" : "flex-start"}`,
    textAlign: `${$center ? 'center' : 'left'}`,
    fontSize: '13px',
    fontFamily: $isNumeric ? font.lato : font.raleway,
    verticalAlign: 'middle',
    fontWeight: 500,
    color: theme?.CorporateTheme?.colors?.textSecondary,
    // maxWidth: '200px',
    // overflow: 'hidden',
    // textOverflow: 'ellipsis',
    // whiteSpace: 'nowrap',
    // wordBreak: 'break-word',
    transition: 'all 0.3s ease', // Add this line
    // '& p': {
    //   display: '-webkit-box',
    //   overflow: 'hidden',
    //   textOverflow: 'ellipsis',
    //   '-webkit-line-clamp': 5, // number of lines
    //   '-webkit-box-orient': 'vertical',
    // },
  })
);


export const FlagBadge = styled.span<{ $color?: string; $circleColor?: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${CorporateTheme.spacing.sm};
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: "Raleway", sans-serif;
  background: ${({ $circleColor }) =>
    $circleColor ? `${hexToRgba($circleColor, 0.1)}` : "#0000001a"}; // 0.1 = 10% opacity
  color: ${({ $circleColor }) => $circleColor};
  position: relative;

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    background: ${({ $circleColor }) => $circleColor};
    border-radius: 50%;
  }
`;

const ProfileContainer = styled.div<{ $center?: boolean; }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => $center ? 'center' : 'left'};
  gap: ${CorporateTheme.spacing.sm};
`;

export const ProfileImage = styled.div<{ $src?: string }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${CorporateTheme.colors.background};
  min-width: 30px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
    padding: 1px; /* Creates spacing between image and border */
  }

  &::after {
    content: "";
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border: 1px solid ${CorporateTheme.colors.border};
    border-radius: 50%;
  }
`;

export const AssigneeImage = styled.div<{ $src?: string }>`

display: flex;
align-items: center;
justify-content: center;

span {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -5px;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  background: ${({ theme }) => theme.CorporateTheme.colors.background};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
  }
  &.img-avatar{
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary}
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG}
  }
}
`;

const NameContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontSize: '13px',
  color: theme.CorporateTheme.colors.textPrimary,
  fontFamily: '"Raleway", sans-serif',
  overflow: 'hidden',
  // gap: CorporateTheme.spacing.xs,
}));

const NameText = styled.span`
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const IdText = styled('span')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 400,
  fontFamily: '"Lato", sans-serif',
  color: theme.CorporateTheme.colors.textSecondary,
  textAlign: 'left',
}));

const FlexCenterDiv = styled.div<{ $center?: boolean, $gap?: string }>`
  display: flex;
  align-items: center;
  text-align: left;
  gap:  ${({ $gap }) => $gap || CorporateTheme.spacing.sm};
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
  white-space: nowrap;
  // width: max-content;
`;

const ActionButton = styled('button')<{ $variant?: 'delete' }>(
  ({ theme, $variant }) => {
    const isDelete = $variant === 'delete';
    return {
      padding: theme.CorporateTheme.spacing.xs,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '0.875rem',
      borderRadius: theme.CorporateTheme.borderRadius,
      transition: 'all 0.2s',
      color: isDelete
        ? theme.CorporateTheme.colors.danger
        : theme.CorporateTheme.colors.headerColor,
      '&:hover': {
        background: isDelete
          ? 'rgba(231, 76, 60, 0.1)'
          : 'rgba(41, 128, 185, 0.1)',
      },
    };
  }
);

const ActionTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
}));

const FlexDiv = styled.div`
  display: flex;
  gap: ${CorporateTheme.spacing.sm};
  justify-content: center;
  width: 100%;
`;

interface ContactRole {
  contactOrganization?: {
    contact?: {
      id: number;
      image?: string;
      firstName: string;
      lastName: string;
      email: string;
      signedUrl: string;
    };
  };
}

const DataTable: FC<DataTableProps> = ({
  columns,
  data,
  forms,
  components,
  hideIdColumn,
  isFields,
  noDataTitle,
  noDataSubtitle,
  columnOrder,
  visibleColumns,
  selectedIds,
  sort,
  onDragEnd,
  onSort,
  onSelectAll,
  onSelectRow,
  onAction,
  loading,
  isFetching,
  isFetchComplete = false,
  noAction,
  perPage,
  minWidth,
  title,
  profiledName,
  dynamicColumnsListing = false,
  skeletonHeight,
  showCheckboxColumn = false,
  routeKey,
  hideActionColumn = false,
  containerHeight = 270,
  filterHeight = 0,
  loadingID,
  istableCellSpacing = false
}) => {
  const { theme } = useTheme();
  const [showNoData, setShowNoData] = useState(false);
  const navigate = useNavigate();
  const [showSkeleton, setShowSkeleton] = useState(false);

  const noDataRef = useRef<HTMLDivElement>(null);
  // const handleRowClick = useCallback((id: string | number) => {
  //   onSelectRow?.(Number(id));
  // }, [onSelectRow]);

  const dedupeContacts = (roles: ContactRole[]) => {
    const seen = new Set<number>();
    return roles?.filter(role => {
      const id = role.contactOrganization?.contact?.id;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    }) ?? [];
  };

  const handleRowClick = (id: number, type: "contact" | "organization" | null, status?: string, isStandard?: boolean) => {

    if (routeKey === "preview") return

    if (routeKey === "records") {
      if (type === "contact") {
        navigate(`?${new URLSearchParams({ page: "contactDetails", id: String(id) }).toString()}`);
      } else if (type === "organization") {
        navigate(`?${new URLSearchParams({ page: "organizationDetails", id: String(id) }).toString()}`);
      }
      return; // stop here for records route
    }

    // If this table is rendering fields, clicking a row should open the field viewer/editor (via onAction)
    if (isFields) {
      onAction && onAction("view", String(id));
      return;
    }

    if (title?.includes("Contacts Listing")) {
      navigate(`?${new URLSearchParams({ page: "contactDetails", id: String(id) }).toString()}`);
    } else if (title?.includes("Organizations Listing")) {
      navigate(`?${new URLSearchParams({ page: "organizationDetails", id: String(id) }).toString()}`);
    } else if (routeKey === "importsListing") {
      navigate(`/home/records/${id}`);
    } else if (forms && status !== "PUBLISHED") {
      onAction("edit", String(id));
    } else if (forms && status === "PUBLISHED") {
      onAction("view", String(id));
    } else {
      if (!onAction) return;

      if (routeKey === "widgets") {
        onAction('view', String(id), undefined, isStandard);
      } else {
        onAction('view', String(id));
      }
    }
  };

  const handleCheckboxClick = useCallback((e: React.MouseEvent, id: string | number) => {
    e.stopPropagation();
    onSelectRow?.(Number(id));
  }, [onSelectRow]);
  const renderCellContent = useCallback(
    (column: Column | undefined, value: any, item: any, index: number) => {  // Added index parameter
      if (!column) return null;

      const myID = item?.[Object.keys(item).find((key) => key.endsWith("id")) || "id"];

      if (column.id === "name" && profiledName) {
        const name = title?.includes("Contacts Listing")
          ? item?.["First Name"] + " " + item?.["Last Name"]
          : item?.firstName + " " + item?.lastName;




        return (
          <ProfileContainer
            $center={index !== 0}
            onClick={() => onAction("view", String(myID))}
            className="cursor-pointer"
          >
            {!forms && (
              <ProfileImage $src={item.signedUrl || PlaceholderImage}>
                <img
                  src={item.signedUrl || PlaceholderImage}
                  alt={item.signedUrl}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = PlaceholderImage;
                  }}
                />
              </ProfileImage>
            )}
            <NameContainer theme={theme}>
              <NameText>{item?.name || name}</NameText>
              {!forms && <IdText theme={theme}>Id: {myID}</IdText>}
            </NameContainer>
            {
              item.isPrimary == true ?
                <FlagBadge $circleColor={theme.CorporateTheme.colors.green}>Primary</FlagBadge>
                : null
            }
          </ProfileContainer>
        );
      }
      if (title?.includes("Contact Roles") && column.id === "name") {
        return (
          <>
            <span>{item.name + "  " || "-"}</span>
            {item.isStandard ?
              <StyledToolTip title={`${item.name} is a system role`} childrenClass={'ms-0'}>
                <TooltipIcon color="#767476" />
              </StyledToolTip>
              : null
            }
          </>
        )
      }
      if (column.id === "primaryUser") {
        if (item?.contactId) {
          return (
            <ProfileContainer $center={index !== 0}>
              <ProfileImage $src={item?.contactSignedUrl || PlaceholderImage}>
                <img
                  src={item?.contactSignedUrl || PlaceholderImage}
                  alt={item.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = PlaceholderImage;
                  }}
                />
              </ProfileImage>
              <NameContainer theme={theme}>
                <NameText>
                  {item?.contactFirstName + " " + item?.contactLastName}
                </NameText>
                <IdText theme={theme}>Id: {item?.contactId}</IdText>
              </NameContainer>
            </ProfileContainer>
          );
        } else return "-";
      }
      if (column.id === "account") {
        if (item?.account) {
          return (
            <ProfileContainer $center={index !== 0} className="d-flex">
              <ProfileImage $src={item?.account?.signedUrl || PlaceholderImage}>
                <img
                  src={item?.account?.signedUrl || PlaceholderImage}
                  alt={item.account?.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = PlaceholderImage;
                  }}
                />
              </ProfileImage>
              <NameContainer theme={theme}>
                <NameText>
                  {item?.account?.firstName + " " + item?.account?.lastName}
                </NameText>
                <IdText theme={theme}>Id: {item?.account?.id}</IdText>
              </NameContainer>
            </ProfileContainer>
          );
        } else return "-";
      }

      // if (column.isFlag) {
      //   const st = (column.id === "isStandard" && item?.isStandard) ? "Standard" : (column.id === "isStandard" && !item?.isStandard) ? "Custom" : capitalize(getStatusLabel(item?.statusId));
      //   return <FlagBadge $circleColor={(st === "Published" || st === "Standard" || st === "Completed") ? "#009828" : "#8F0000"}>{st}</FlagBadge>;
      // }

      if (column.isFlag) {
        // Determine label
        const st = column.id === "isStandard"
          ? (item?.isStandard ? "System" : "Custom")
          : capitalize(getStatusLabel(item?.statusId));

        // Pick color dynamically
        const circleColor = statusColorMapping[st.toUpperCase()] || statusColorMapping.DEFAULT;

        return (
          <FlagBadge $circleColor={circleColor}>
            {st}
            {column.id === "statusId" && routeKey === "importsListing" && st === "Processing" &&
              " (" + getPercentage(item?.totalRecords, item?.totalProcessed) + "%)"}
          </FlagBadge>
        );
      }

      if (column.isTag) {
        const contactOrgRoles = item?.roles || item?.role;
        const roles = contactOrgRoles?.split(',').map((r: string) => r.trim());

        return (
          <div className="d-flex flex-wrap gap-1 align-items-center justify-content-center">
            {roles?.map((r: string, index: number) => (
              r ? (
              <StyledTag key={index} $circleColor={theme.name === 'light' ? getRandomColor(index) : '#ffffff'}>
                {r}
              </StyledTag>
              ) : '-'
            ))}
          </div>
        )
      }

      if (column.isCustomStandardTags) {
        if (column.id === "isStandard") {
          return (
            <StyledTag borderRadius={10} fontSize={12} key={index} $circleColor={item?.isStandard ? theme.CorporateTheme.colors.primary : lightTheme.CorporateTheme.colors.redLabel}>
              {item?.isStandard ? "System" : "Custom"}
            </StyledTag>
          )
        }

      }
      if (["Date", "date", "created_at", "updated_at", "createdAt", "Organization Last Modified", "Contact Last Modified"].some(key => column.id.includes(key))) {
        return (
          <span> {formatDateDefault(value) || "-"}</span>
        );
      }
      if (column.id == "autoAssign") {
        return (
          item.autoAssigned ?
            <span>Yes</span>
            : <span>No</span>
        )
      }
      if (column.id == "contactRoles") {
        const uniqueRoles = dedupeContacts(item?.contactRoles);
        const displayedRoles = uniqueRoles.slice(0, 5);
        const overflowCount = Math.max(0, uniqueRoles.length - 5);
        return (
          item?.contactRoles.length == 0 ?
            <span>Un assigned</span>
            :
            <AssigneeImage theme={theme} className="flex">
              {displayedRoles.map((assi) => {
                const contact = assi.contactOrganization?.contact;
                const fullName = contact?.firstName && contact?.lastName
                  ? `${contact.firstName} ${contact.lastName}`
                  : contact?.firstName || contact?.lastName || contact?.email || 'Unknown';

                return (
                  <StyledToolTip
                    title={fullName}
                    childrenClass={'ms-0'}
                    key={contact?.id ?? `fallback-${Math.random()}`}
                  >
                    <span>
                      <img
                        src={contact?.signedUrl || PlaceholderImage}
                        alt={contact?.firstName || 'User'}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = PlaceholderImage;
                        }}
                      />
                    </span>
                  </StyledToolTip>
                );
              })}

              {overflowCount > 0 && (
                <span className="img-avatar">+{overflowCount}</span>
              )}
            </AssigneeImage>
        );
      }
      if (column.id == "organizationId") {
        return <span> {item?.organizationName || "-"}</span>;
      }
      if (column.id == "roleId") {
        return <span> {item?.roleName || "-"}</span>;
      }
      if (column.id == "totalRecords") {
        return <StyledText fontFamily={font.lato} fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}> {item?.totalRecords || 0}</StyledText>;
      }
      if (column.id == "totalProcessed") {
        return <StyledText fontFamily={font.lato} fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}> {item?.totalProcessed || 0}</StyledText>;
      }
      if (column.id == "errorCount") {
        return <StyledText fontFamily={font.lato} fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}> {item?.errorCount || 0}</StyledText>;
      }
      if (column.id == "duplicateCount") {
        return <StyledText fontFamily={font.lato} fontSize="13px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}> {item?.duplicateCount || 0}</StyledText>;
      }
      // return <p>{value || item[column?.id] || "-"}</p>;
      return <TruncatedText text={value || item[column?.id]} />;
    },
    [title, profiledName, forms, theme]
  );
  // useEffect(() => {
  //   if (!loading && !isFetching && data?.length === 0) {
  //     const timeout = setTimeout(() => setShowNoData(true), 0);
  //     return () => { clearTimeout(timeout); setShowNoData(false) }

  //   }
  // }, [loading, isFetching, data]);

  useEffect(() => {
    // Agar loading ho rahi hai ya fetch adjust hone wala hai → no-data show na karo
    if (loading || isFetching) {
      setShowSkeleton(true);

      setShowNoData(false);
      return;
    }
    setShowSkeleton(false);
    if (!data || data?.length === 0) {
      // Thoda delay do taake page-adjust hone ka chance mile
      const timeout = setTimeout(() => setShowNoData(true), 300); // 300ms delay
      return () => clearTimeout(timeout);
    } else {
      setShowNoData(false);
    }
  }, [loading, isFetching, data]);

  if (showNoData) {
    return (
      <NoData
        ref={noDataRef}
        title={noDataTitle || "No Data Found"}
        subtitle={noDataSubtitle || "No data available for the selected filters."}
        containerHeight={containerHeight}
        filterHeight={filterHeight}
      />
    );
  }

  const visibleColumnIds = columnOrder?.filter(id => visibleColumns.includes(id)) || [];

  const renderHeader = () => {
    if (routeKey === "preview") return null;
    if (routeKey === "records") return null;
    if (hideActionColumn) return null;
    return (<TableHeaderCell theme={theme} style={{ textAlign: "center" }}>
      <label > {visibleColumnIds.length > 0 && 'Actions'}</label>
    </TableHeaderCell>)
  }

  const renderActionButton = (actionType: "delete", myID: string) => {

    if (routeKey === "preview") return null;
    if (routeKey === "records") return null;
    if (hideActionColumn) return null;
    return (<ActionTableCell>
      <ActionButton
        theme={theme}
        title={actionType ? `${actionType.charAt(0).toUpperCase() + actionType.slice(1)}` : 'Action'}
        onClick={(e) => { e.stopPropagation(); onAction(actionType, myID) }}
        $variant="delete"
      >
        <DeleteIcon />
      </ActionButton>
    </ActionTableCell>)
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <TableScrollWrapper
        containerHeight={containerHeight > 0 ? `${containerHeight + filterHeight + 15}px` : undefined}
      >
        <Table width={minWidth} style={{ overflowX: 'hidden' }}>
          {showSkeleton ? (
            <TableHeader theme={theme}>
              <tr>
                {showCheckboxColumn && <TableHeaderCell theme={theme}><SkeletonCell height={skeletonHeight} /></TableHeaderCell>}
                {visibleColumnIds.map((columnId) => (
                  <TableHeaderCell key={`skeleton-header-${columnId}`} theme={theme}>
                    <SkeletonCell height={skeletonHeight} />
                  </TableHeaderCell>
                ))}
                {!hideActionColumn && <TableHeaderCell theme={theme}><SkeletonCell height={skeletonHeight} /></TableHeaderCell>}
              </tr>
            </TableHeader>
          ) : (
            <TableHeader theme={theme}>
              <Droppable droppableId="columns" direction="horizontal">
                {(provided) => (
                  <tr {...provided.droppableProps} ref={provided.innerRef}>
                    {showCheckboxColumn &&
                      <TableHeaderCell theme={theme}>
                        {/* <input
                          className="mt-2"
                          type="checkbox"
                          checked={data.length > 0 && selectedIds?.length === data.length}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              const ids = data.map((item, index) => {
                                const myID = item?.[Object.keys(item).find((key) => key.endsWith("id")) || "id"];
                                return myID ?? Number(myID);
                              });
                              onSelectAll?.(ids);
                            } else {
                              onSelectAll?.([]);
                            }
                          }}
                          disabled={data.length === 0}
                        /> */}
                        <StyledCheckBox
                          className="cursor-pointer rounded-1"
                          size={16}
                          borderColor={
                            data.length > 0 && selectedIds?.length === data.length
                              ? theme.CorporateTheme?.colors?.lightNavy
                              : "#626262"
                          }
                          // color={checkboxLabelColor}
                          bgColor={theme.CorporateTheme?.colors?.lightNavy}
                          checkColor={theme.colors.contentBackground}
                          value={data.length > 0 && selectedIds?.length === data.length}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (e.target.checked) {
                              const ids = data.map((item, index) => {
                                const myID = item?.[Object.keys(item).find((key) => key.endsWith("id")) || "id"];
                                return myID ?? Number(myID);
                              });
                              onSelectAll?.(ids);
                            } else {
                              onSelectAll?.([]);
                            }
                          }}
                          disabled={data.length === 0}
                        />
                      </TableHeaderCell>}

                    {visibleColumnIds.map((columnId, index) => {
                      const column = columns?.find(c => c.id === columnId);
                      return (
                        <Draggable
                          key={column?.id || columnId}
                          draggableId={column?.id || columnId}
                          index={index}
                        >
                          {(provided) => (
                            <TableHeaderCell
                              theme={theme}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                              $isDraggable
                            >
                              <FlexCenterDiv
                                // $center={column?.id !== "name"}
                                $center={index !== 0}
                                onClick={() => {
                                  if (column?.isSortable) {
                                    onSort?.(column.id);
                                  }
                                }}
                              >
                                {title?.includes("Contacts Listing") &&
                                  column?.label === "First Name"
                                  ? formatColumnLabel("Name")
                                  : formatColumnLabel(column?.label || "...")}

                                {(column?.isTooltip && column?.TooltipText) && (
                                  <StyledToolTip title={column?.TooltipText}>
                                    <TooltipIcon size={18} color={theme.CorporateTheme.colors.gunmetal} />
                                  </StyledToolTip>
                                )}

                              </FlexCenterDiv>
                            </TableHeaderCell>
                          )}
                        </Draggable>
                      );
                    })}

                    {/* {routeKey === "preview" ? null : <TableHeaderCell theme={theme} style={{ textAlign: "center" }}>
                      <label > {visibleColumnIds.length > 0 && 'Actions'}</label>
                    </TableHeaderCell>} */}
                    {renderHeader()}

                    {provided.placeholder}
                  </tr>
                )}
              </Droppable>
            </TableHeader>
          )}

          <tbody>
            {showSkeleton ? (
              Array.from({ length: perPage || 10 }).map((_, rowIdx) => (
                <TableRow key={`skeleton-${rowIdx}`} theme={theme}>
                  {visibleColumnIds.map((columnId, colIdx) => (
                    <TableCell key={`skeleton-${columnId}-${colIdx}`} theme={theme} $center={columnId !== "name"}>
                      <SkeletonCell height={skeletonHeight} />
                    </TableCell>
                  ))}
                  {hideActionColumn ? null : <ActionTableCell theme={theme}>
                    <FlexDiv>
                      {[...Array(3)].map((_, i) => (
                        <SkeletonCell key={`action-skeleton-${i}`} height={skeletonHeight} />
                      ))}
                    </FlexDiv>
                  </ActionTableCell>}
                </TableRow>
              ))
            ) : (
              (forms ? data?.filter(dt => !dt?.isDeleted) : data)?.map((item, index) => {
                const myID = item?.[Object.keys(item).find((key) => key.endsWith("id")) || "id"];

                // 🔹 Force type = "organization" when title indicates Organizations Listing
                let type: "contact" | "organization" | null = null;
                const hasContactId = Object.keys(item).some(key => key.endsWith("contact_id"));
                const hasOrgId = Object.keys(item).some(key => key.endsWith("organization_id"));
                if (hasContactId) {
                  type = "contact";
                } else if (hasOrgId || title?.includes("Organizations Listing")) {
                  type = "organization"; // ✅ Primary fix: fallback to "organization" if in org listing
                }

                return (
                  <TableRow
                    key={myID}
                    theme={theme}
                    onClick={() => handleRowClick(myID, type || item?.scope, getStatusLabel(item.statusId), item?.isStandard)}
                    $selected={selectedIds?.includes(myID)}
                  >
                    {showCheckboxColumn &&
                      <TableCell theme={theme} istableCellSpacing={istableCellSpacing}>
                        {/* <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedIds?.includes(myID)}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow && onSelectRow(myID);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        /> */}

                        <StyledCheckBox
                          className="cursor-pointer rounded-1"
                          size={16}
                          borderColor={
                            selectedIds?.includes(myID)
                              ? theme.CorporateTheme?.colors?.lightNavy
                              : "#626262"
                          }
                          // color={checkboxLabelColor}
                          bgColor={theme.CorporateTheme?.colors?.lightNavy}
                          checkColor={theme.colors.contentBackground}
                          value={!!selectedIds?.includes(myID)}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow && onSelectRow(myID);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>}

                    {visibleColumnIds.map((columnId, index) => {
                      const column = columns?.find((c) => c.id === columnId);
                      const selectedCol = column?.label;
                      const cellValue = dynamicColumnsListing
                        ? column && selectedCol
                          ? item[selectedCol]
                          : "-"
                        : item[columnId];
                      const isNumeric = typeof cellValue === "number" || /^[0-9.,]+$/.test(cellValue);

                      return (
                        <TableCell
                          key={`${myID}-${columnId}`}
                          theme={theme}
                          $isNumeric={isNumeric}
                          istableCellSpacing={istableCellSpacing}
                          // $center={columnId !== "name"}
                          $center={index !== 0}
                        // onClick={() => handleRowClick(myID)}
                        >
                          {dynamicColumnsListing
                            ? column && selectedCol
                              ? renderCellContent(column, item[selectedCol], item, index)
                              : "-"
                            : renderCellContent(column, item[columnId], item, index)}
                        </TableCell>
                      );
                    })}

                    {(!noAction) ? (
                      <ActionTableCell theme={theme}>
                        <div onClick={(e) => e.stopPropagation()}>
                          <FlexDiv>

                            {title === "Import History" ? (

                              // item?.statusId === 56 ? null : <Button theme={theme} type="button" $variant="customButton" fontSize="12px" height="28px" btnBg={CorporateTheme.colors.headerBG} onClick={() => onAction("view", String(myID))}>
                              //   View Records
                              // </Button>
                              item?.statusId === 1 ?
                                <div className="d-flex align-items-center gap-1">
                                  <div onClick={() => navigate(`/home/mapping/${item?.id}/${item.scopeId}`)}><ContinueIcon color={theme.CorporateTheme.colors.gunmetal} size={20} /></div>
                                  <div onClick={(e) => { e.stopPropagation(); onAction("delete", String(myID)) }}><DeleteIcon color={theme.CorporateTheme.colors.gunmetal} size={20} /></div>
                                </div>
                                :
                                <div onClick={(e) => { e.stopPropagation(); onAction("view", String(myID), item?.scope) }}>
                                  <ViewIcon color={theme.CorporateTheme.colors.textSecondary} />
                                </div>
                            ) :
                              title === "Ignore" ? (
                                <Button
                                  theme={theme}
                                  type="button"
                                  $variant="preview"
                                  fontSize="12px"
                                  height="28px"
                                  className={item.contactId === item.sourceID || item.org_ID === item.sourceID ? 'cursor-not-allowed' : ''}
                                  onClick={(e) => { e.stopPropagation(); onAction("ignore", String(item.contactId ?? item.org_ID)) }}
                                  disabled={item.contactId === item.sourceID || item.org_ID === item.sourceID ? true : false}
                                  style={{
                                    opacity: item.contactId === item.sourceID || item.org_ID === item.sourceID ? '0.5' : '1',
                                  }}
                                >
                                  {loadingID === item.contactId || loadingID === item.org_ID ?
                                    <LoadingCircleIcon color={theme.CorporateTheme.colors.secondary} />
                                    : null}
                                  Ignore
                                </Button>
                              ) : title === "Widgets" && !item?.isStandard ? <div className="d-flex align-items-center gap-3">
                                <div onClick={(e) => { e.stopPropagation(); onAction("edit", String(myID)) }}><EditIcon color={theme.CorporateTheme.colors.gunmetal} size={18} /></div>
                                <div onClick={(e) => { e.stopPropagation(); onAction("delete", String(myID)) }}><DeleteIcon color={theme.CorporateTheme.colors.gunmetal} size={18} /></div>
                              </div> : title === "Widgets" && item?.isStandard ?
                                <div className="d-flex align-items-center gap-3">
                                  <div onClick={(e) => { e.stopPropagation(); onAction("view", String(myID), undefined, item?.isStandard) }}><EditIcon color={theme.CorporateTheme.colors.gunmetal} size={18} /></div>
                                  <div onClick={(e) => { e.stopPropagation(); onAction("duplicate", String(myID)) }}><CopyIcon color={theme?.CorporateTheme?.colors?.textSecondary} /></div>
                                </div>
                                : (
                                  <>
                                    {/* {isFields && !item?.isStandard && ConditionalFields?.includes(item?.dataType) && (
                                  <ActionButton theme={theme} onClick={(e) => { e.stopPropagation(); onAction("condition", String(myID)) }}>
                                    <ConditionalFieldIcon color={theme.CorporateTheme.colors.textSecondary} />
                                  </ActionButton>
                                )} */}



                                    {!title?.includes("Contact Roles") && (
                                      <ActionButton theme={theme} title="Copy" onClick={(e) => { e.stopPropagation(); onAction("copy", String(myID)) }}>
                                        <CopyIcon color={theme?.CorporateTheme?.colors?.textSecondary} />
                                      </ActionButton>
                                    )}

                                    {!forms && !isFields && !components && !title?.includes("Contact Roles") && (
                                      <ActionButton theme={theme} title="Sync" onClick={(e) => { e.stopPropagation(); onAction("sync", String(myID)) }}>
                                        <SynconIcon color={theme.CorporateTheme.colors.textSecondary} />
                                      </ActionButton>
                                    )}

                                    {!isFields && !components && !title?.includes("Contact Roles") && (
                                      <ActionButton theme={theme} title="View" onClick={(e) => { e.stopPropagation(); onAction("view", String(myID)) }}>
                                        <ViewIcon color={theme.CorporateTheme.colors.textSecondary} />
                                      </ActionButton>
                                    )}

                                    {(!item?.isStandard &&
                                      (isFields || components || (forms && getStatusLabel(item.statusId) !== "PUBLISHED") || title?.includes("Contact Roles"))) && (
                                        <ActionButton theme={theme} title="Edit" onClick={(e) => { e.stopPropagation(); onAction("edit", String(myID)) }}>
                                          <EditIcon color={theme.CorporateTheme.colors.textSecondary} />
                                        </ActionButton>
                                      )}

                                    {(isFields || components || forms) &&
                                      !item?.isStandard &&
                                      (forms ? getStatusLabel(item.statusId) !== "PUBLISHED" : true) && (
                                        <ActionButton
                                          theme={theme}
                                          title="Delete"
                                          onClick={(e) => { e.stopPropagation(); onAction("delete", String(myID)) }}
                                          $variant="delete"
                                        >
                                          <DeleteIcon />
                                        </ActionButton>
                                      )}

                                    {!forms && !isFields && !components && !title?.includes("Contact Roles") && (
                                      <ActionButton theme={theme} title="Share" onClick={(e) => { e.stopPropagation(); onAction("share", String(myID)) }}>
                                        <ShareLinkIcon color={theme.CorporateTheme.colors.textSecondary} />
                                      </ActionButton>
                                    )}

                                    {title?.includes("Contact Roles") && (item.roleType === "Custom" && item.rolePermissions.length > 0) && (
                                      <ActionButton
                                        theme={theme}
                                        title="Revoke"
                                        onClick={(e) => { e.stopPropagation(); onAction("revoke", String(myID)) }}
                                        disabled={item.roleType !== "Custom"}
                                      >
                                        <BlockIcon color={theme.CorporateTheme.colors.textSecondary} />
                                      </ActionButton>
                                    )}

                                </>)}
                          </FlexDiv>
                        </div>
                      </ActionTableCell>

                    ) : (
                      // <ActionTableCell>
                      //   <ActionButton
                      //     theme={theme}
                      //     onClick={() => onAction("delete", String(myID))}
                      //     $variant="delete"
                      //   >
                      //     <DeleteIcon />
                      //   </ActionButton>
                      // </ActionTableCell>
                      renderActionButton("delete", String(myID))
                    )}

                  </TableRow>
                );
              })
            )}
          </tbody>
        </Table>
      </TableScrollWrapper>
    </DragDropContext >
  );

};

export default DataTable;

const TableScrollWrapper = styled(SimpleBar) <{ containerHeight?: string }>`
  // max-width: 100%;

  // /* Default */
  // max-height: calc(75vh - 60px); /* subtract pagination height */

  // @media (max-width: 1440px) {
  //   max-height: calc(65vh - 60px);
  // }

  // @media (max-width: 1200px) {
  //   max-height: calc(55vh - 60px);
  // }

  // @media (max-width: 992px) {
  //   max-height: calc(45vh - 60px);
  // }

  // @media (max-width: 768px) {
  //   max-height: calc(40vh - 60px);
  // }

  // @media (max-width: 576px) {
  //   max-height: calc(35vh - 60px);
  // }

  //max-height: ${({ containerHeight }) => containerHeight ? `calc(100vh - ${containerHeight})` : 'none'};
  //overflow-y: ${({ containerHeight }) => (containerHeight ? 'auto' : 'visible')};

  max-height: 100%;
  overflow-y: auto;

`;
